import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CustomerService } from '../../../api/gen/api/customer.service';
import { CreateCustomerRequest } from '../../../api/gen/model/createCustomerRequest';
import {
    JjamesButtonComponent, JJamesInputComponent, JJamesCardComponent,
    JJamesAlertComponent
} from 'shared-lib';

@Component({
    selector: 'app-create-customer',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        JjamesButtonComponent,
        JJamesInputComponent,
        JJamesCardComponent,
        JJamesAlertComponent
    ],
    templateUrl: './create-customer.component.html',
    styleUrls: ['./create-customer.component.scss']
})
export class CreateCustomerComponent {
    private readonly fb = inject(FormBuilder);
    private readonly customerService = inject(CustomerService);
    private readonly router = inject(Router);

    createCustomerForm: FormGroup;
    isSubmitting = false;
    errorMessage: string | null = null;
    successMessage: string | null = null;

    constructor() {
        this.createCustomerForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]]
        });
    }

    onSubmit(): void {
        if (this.createCustomerForm.valid && !this.isSubmitting) {
            this.isSubmitting = true;
            this.errorMessage = null;
            this.successMessage = null;

            const request: CreateCustomerRequest = {
                name: this.createCustomerForm.value.name
            };

            this.customerService.customerPost(request).subscribe({
                next: (response) => {
                    this.isSubmitting = false;
                    if (response) {
                        this.successMessage = 'Customer created successfully! Four operator groups have been created automatically.';
                        setTimeout(() => {
                            this.router.navigate(['/customer-management']);
                        }, 2000);
                    }
                },
                error: (error) => {
                    this.isSubmitting = false;
                    this.errorMessage =  error.error.title || 'An error occurred while creating the customer. Please try again.';
                    console.error('Error creating customer:', error);
                }
            });
        } else {
            this.markFormGroupTouched(this.createCustomerForm);
        }
    }

    onCancel(): void {
        this.router.navigate(['/customer-management']);
    }

    onClearError(): void {
        this.errorMessage = null;
    }

    onClearSuccess(): void {
        this.successMessage = null;
    }

    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.keys(formGroup.controls).forEach(field => {
            const control = formGroup.get(field);
            control?.markAsTouched({ onlySelf: true });
        });
    }

    isFieldInvalid(fieldName: string): boolean {
        const field = this.createCustomerForm.get(fieldName);
        return !!(field && field.invalid && field.touched);
    }

    getFieldError(fieldName: string): string {
        const field = this.createCustomerForm.get(fieldName);
        if (field && field.errors && field.touched) {
            if (field.errors['required']) return 'Customer name is required';
            if (field.errors['minlength']) return 'Customer name is too short (minimum 2 characters)';
            if (field.errors['maxlength']) return 'Customer name is too long (maximum 100 characters)';
        }
        return '';
    }
}
